<template>
  <div class="w-full h-full flex flex-row gap-5">
    <doughnut-chart
      v-if="doughnutChartData"
      class="p-3 w-1/4 h-full"
      :data="doughnutChartData"
      :options="doughnutChartOptions"
      :plugins="doughnutChartPlugins"
    />
    <bar-chart
      v-if="barChartData"
      :key="barChartData.labels[0]"
      class="w-3/4 h-full"
      :data="barChartData"
      :options="barChartOptions"
      :plugins="barChartPlugins"
    />
    <div
      v-if="!doughnutChartData && !barChartData"
      class="w-full h-32 sel-center flex items-center justify-center"
    >
      <span class="text-grey-001">No Data Found</span>
    </div>
  </div>
</template>

<script>
import {sum, upperFirst} from "lodash";

import BarChart from "@/components/barChart";
import DoughnutChart from "@/components/doughnutChart";
import {tooltipConfig} from "@/utils/chartUtils";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";

export default {
  props: {
    summaryData: {
      type: Object,
      default: () => {}
    },
    rankData: {
      type: Array,
      default: () => []
    },
    cycleTimeTarget: {
      type: Number,
      default: null
    }
  },
  components: {
    DoughnutChart,
    BarChart
  },
  data() {
    return {
      isReady: false,
      doughnutChartData: null,
      barChartData: null
    }
  },
  watch: {
    summaryData: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          if (Object.keys(val).every((key) => val[key] === null)) {
            this.doughnutChartData = null
            return
          }
          this.doughnutChartData = {
            labels: Object.keys(val).map(key => key.split('_')[1]),
            datasets: [
              {
                data: Object.keys(val).map(key => val[key] !== 0 ? val[key] : null),
                backgroundColor: Object.keys(val).map((key) => {
                  const label = key.split('_')[1]
                  switch (label) {
                    case 'fuel':
                      return '#AF4DDD'
                    case 'sailing':
                      return '#50CD89'
                    case 'loading':
                      return '#3E97FF'
                    case 'unloading':
                      return '#7239EA'
                    default:
                      return '#AF4DDD'
                  }
                })
              }
            ]
          }
        } else {
          this.doughnutChartData = null
        }
      }
    },
    rankData: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val.length && this.summaryData) {
          if (Object.keys(val).every((key) => val[key] === null)) {
            this.barChartData = null
            return
          }
          const datasets = [
            {
              type: 'line',
              label: 'Target',
              data: val.map(() => this.cycleTimeTarget),
              borderColor: '#EE443F',
              borderWidth: 2,
              borderDash: [5, 5],
              pointRadius: 5,
              pointBorderWidth: 2,
              fill: false,
              pointHoverRadius: 5,
              pointHitRadius: 30
            },
            {
              label: 'Fuel',
              data: val.map((item) => item.avg_fuel_bunker_trip_days),
              backgroundColor: '#AF4DDD',
              ownerName: val.map((item) => item.owner_name)
            },
            {
              label: 'Sailing',
              data: val.map((item) => item.avg_sailing_trip_days),
              backgroundColor: '#50CD89',
              ownerName: val.map((item) => item.owner_name)
            },
            {
              label: 'Loading',
              data: val.map((item) => item.avg_loading_trip_days),
              backgroundColor: '#3E97FF',
              ownerName: val.map((item) => item.owner_name)
            },
            {
              label: 'Unloading',
              data: val.map((item) => item.avg_unloading_trip_days),
              backgroundColor: '#7239EA',
              ownerName: val.map((item) => item.owner_name)
            }
          ]

          this.barChartData = {
            labels: val.map((item) => item.owner_code),
            datasets: [
              ...datasets
            ]
          }
        } else {
          this.barChartData = null
        }
      }
    }
  },
  computed: {
    doughnutChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        borderWidth: 0,
        plugins: {
          legend: {
            display: false
          },
          tooltip: tooltipConfig({
            callbacks: {
              title(titleItem) {
                const { label } = titleItem[0]
                return upperFirst(label)
              },
              label(tooltipItem) {
                const data = tooltipItem?.dataset?.data
                const dataIndex = tooltipItem?.dataIndex
                const sumData = sum(data)
                const percentage = handleValidateAndFormatNumber(data[dataIndex] / sumData * 100)
                const days = handleValidateAndFormatNumber(data[dataIndex], 2)
                return [
                  `${percentage} %`,
                  `${days} days`
                ]
              }
            }
          })
        },
        layout: {
          padding: {
            bottom: 60
          }
        }
      }
    },
    doughnutChartPlugins() {
      return [
        this.htmlLegendPlugin(),
        this.drawPercentagePlugin()
      ]
    },
    barChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        barPercentage: 0.8,
        borderRadius: {
          topLeft: 5,
          topRight: 5,
          bottomLeft: 5,
          bottomRight: 5
        },
        borderSkipped: 'middle',
        layout: {
          padding: {
            left: 25,
            top: 30,
            right: 25
          }
        },
        scales: {
          x: {
            stacked: true,
            offset: true,
            beginAtZero: true,
            grid: {
              display: false
            },
            ticks: {
              color: 'black',
              font: {
                family: 'Poppins'
              }
            }
          },
          y: {
            beginAtZero: true,
            stacked: true,
            grid: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          customTitle: {
            y: {
              display: true,
              text: 'Days',
              color: '#000000',
              font: {
                family: 'Poppins',
                size: 12
              }
            }
          },
          axisIcons: true,
          tooltip: tooltipConfig({
            callbacks: {
              title(titleItem) {
                const item = titleItem[0]
                const dataIndex = item?.dataIndex
                const chartType = item?.dataset?.type
                return chartType === 'line' ? 'Target' : item?.dataset?.ownerName[dataIndex]
              },
              label(tooltipItem) {
                const chartType = tooltipItem?.dataset?.type
                const activity = tooltipItem?.dataset?.label
                const value = handleValidateAndFormatNumber(tooltipItem?.raw)
                return chartType === 'line'
                  ? `${value} days`
                  : `${activity}: ${value} days`
              }
            }
          })
        }
      }
    },
    barChartPlugins() {
      return [
        this.chartAreaBorderPlugin(),
        this.customTitlePlugin(),
        this.axisIconPlugin()
      ]
    }
  },
  methods: {
    htmlLegendPlugin() {
      return {
        id: 'custom-legend',
        beforeDraw(chart) {
          const ctx = chart.ctx;
          const legendOpts = chart.options.plugins.legend;
          if (!legendOpts) return;

          const legendItems = chart.legend.legendItems;
          const chartArea = chart.chartArea;

          // Calculate position at bottom center
          const legendX = chartArea.left;
          const legendY = chartArea.bottom + 20;
          const itemWidth = 100; // Width of each legend item
          const itemHeight = 20;
          const itemsPerRow = Math.ceil(legendItems.length / 2);

          ctx.save();
          ctx.font = '12px Poppins';

          // Draw legend items in two rows
          legendItems
            .sort((a, b) => a.text.localeCompare(b.text))
            .forEach((item, i) => {
              const row = Math.floor(i / itemsPerRow);
              const col = (i + 1) % itemsPerRow;

              const x = legendX + chartArea.width / 2  - col * itemWidth;
              const y = legendY + row * (itemHeight + 5);

              // Draw colored box
              ctx.beginPath();
              ctx.roundRect(x, y, 34, 10, 5);
              ctx.fillStyle = item.fillStyle;
              ctx.fill();

              // Draw text
              ctx.fillStyle = '#666';
              ctx.fillText(item.text.charAt(0).toUpperCase() + item.text.slice(1), x + 40, y + 10);
            });

          ctx.restore();
        }
      }
    },
    drawPercentagePlugin() {
      return {
        id: 'drawPercentage',
        afterDatasetsDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          const meta = chart.getDatasetMeta(0);

          meta.data.forEach((arc, index) => {
            const value = data.datasets[0].data[index];
            if (!value) {
              return
            }
            const calculated = handleValidateAndFormatNumber(((value / total) * 100), 2)
            const percentage = `${calculated}%`;

            const {x, y} = arc.tooltipPosition(); // center of each arc

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.font = '600 12px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(percentage, x, y);
            ctx.restore();
          });
        }
      }
    },
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart
          const extension = 15
          const padding = 1

          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2

          ctx.beginPath();
          ctx.moveTo(left, top - extension)
          ctx.lineTo(left, top + height + padding)
          ctx.stroke()

          ctx.beginPath()
          ctx.moveTo(left, top + height + padding)
          ctx.lineTo(left + width + extension, top + height + padding)
          ctx.stroke();

          ctx.restore()
        }
      }
    },
    customTitlePlugin() {
      return {
        id: 'customTitle',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, width } } = chart

          if (options?.y?.display) {
            ctx.save()
            ctx.fillStyle = options.y.color || 'black'

            const font = options.y.font || {}
            const fontSize = font.size || 10;
            const fontFamily = font.family || 'sans-serif'
            const fontWeight = font.weight || 'normal'
            ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`

            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'

            const xPos = left / 2
            const yPos = top - 25

            ctx.fillText(options.y.text, xPos, yPos)
            ctx.restore()
          }
        }
      }
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;

          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top - 15,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };

          const rightIcon = {
            text: '\u2BC8',
            x: right + 15,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }

          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();

          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>